import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { DownloadIcon, UploadV2Icon } from "../Common/assets/Icons";
import { DrawerHeader, CommonDrawerStyle } from "../Common/styles/drawer";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";
import { v4 as uuid } from "uuid";

type ImportStep =
  | "upload"
  | "validating"
  | "preview_errors"
  | "preview_success"
  | "importing"
  | "done";

type PreviewError = {
  row: number;
  email: string;
  field: string;
  message: string;
};

type PreviewRow = {
  row: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phoneNumber: string | null;
  notes?: string | null;
};

type PreviewResponse = {
  totalRows: number;
  validCount: number;
  errorCount: number;
  errors: PreviewError[];
  preview: PreviewRow[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
};

const PAGE_SIZE = 20;

const ImportParticipantsDrawer = ({ open, onClose, onImportComplete }: Props) => {
  const [step, setStep] = useState<ImportStep>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState<string>(uuid());
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [importedCount, setImportedCount] = useState<number>(0);
  const [resultCsvUrl, setResultCsvUrl] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Infinite scroll — load more preview rows as user scrolls
  useEffect(() => {
    if (step !== "preview_success" || !previewData) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + PAGE_SIZE, previewData.preview.length)
          );
        }
      },
      { threshold: 0.1 }
    );
    const sentinel = sentinelRef.current;
    if (sentinel) observer.observe(sentinel);
    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [step, previewData]);

  // Notify parent after import is complete
  useEffect(() => {
    if (step === "done") {
      onImportComplete();
    }
  }, [step, onImportComplete]);

  const resetState = () => {
    setStep("upload");
    setFile(null);
    setFileInputKey(uuid());
    setPreviewData(null);
    setImportedCount(0);
    setResultCsvUrl(null);
    setVisibleCount(PAGE_SIZE);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const selected = e.target.files[0];
    if (selected.size / 1024 / 1024 >= 5) {
      toastMessage("warning", "File size must be smaller than 5MB.");
      setFileInputKey(uuid());
      return;
    }
    setFile(selected);
  };

  const handleValidate = useCallback(async () => {
    if (!file) return;
    setStep("validating");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await http.post(
        "/admin/registry/participants/bulk-upload/preview",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const data: PreviewResponse = res.data.data;
      setPreviewData(data);
      setVisibleCount(PAGE_SIZE);
      setStep(data.errorCount > 0 ? "preview_errors" : "preview_success");
    } catch (err: any) {
      errorToastMessage(err);
      setStep("upload");
    }
  }, [file]);

  const handleReupload = () => {
    setFile(null);
    setPreviewData(null);
    setFileInputKey(uuid());
    setStep("upload");
  };

  const handleImport = useCallback(async () => {
    if (!file) return;
    setStep("importing");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await http.post(
        "/admin/registry/participants/bulk-upload/confirmed",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const data = res.data.data;
      setImportedCount(data.importedCount);
      setResultCsvUrl(data.resultCsvUrl ?? null);
      setStep("done");
    } catch (err: any) {
      errorToastMessage(err);
      // Re-validate step so user can try again with a fixed file
      setStep("preview_success");
    }
  }, [file]);

  const handleDownloadTemplate = async () => {
    try {
      const res = await http.get("/data/participant-import-template", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "participant-import-template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      errorToastMessage(err);
    }
  };

  const handleDownloadResult = () => {
    if (!resultCsvUrl) return;
    const link = document.createElement("a");
    link.href = resultCsvUrl;
    link.download = `import-result-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ ...CommonDrawerStyle, width: "700px", overflowY: "auto" }}>
        <DrawerHeader title="Import Participants" closeDrawer={handleClose} />

        {/* ── Step: upload ─────────────────────────────────────────── */}
        {step === "upload" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert
              severity="info"
              action={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Sample CSV
                </Button>
              }
            >
              Download the sample CSV, fill in your data, then upload. Before
              uploading, <strong>delete the guidance row (row 2) and the
              example row (row 3)</strong> — keep only the header row and your
              data rows.
            </Alert>

            <Box
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <UploadV2Icon />
              <Typography variant="body2" color="text.secondary">
                {file ? file.name : "No file selected"}
              </Typography>
              <Button variant="outlined" component="label">
                {file ? "Change File" : "Select CSV File"}
                <input
                  key={fileInputKey}
                  hidden
                  accept=".csv,text/csv"
                  type="file"
                  onChange={handleFileSelect}
                />
              </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleValidate}
                disabled={!file}
              >
                Validate File
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Step: validating ──────────────────────────────────────── */}
        {step === "validating" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              mt: 6,
            }}
          >
            <CircularProgress />
            <Typography>Validating file…</Typography>
          </Box>
        )}

        {/* ── Step: preview_errors ──────────────────────────────────── */}
        {step === "preview_errors" && previewData && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="error">
              Found <strong>{previewData.errorCount}</strong> error
              {previewData.errorCount !== 1 ? "s" : ""} in{" "}
              <strong>{previewData.totalRows}</strong> row
              {previewData.totalRows !== 1 ? "s" : ""}. Fix the issues below
              and re-upload the file.
            </Alert>

            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Field</TableCell>
                    <TableCell>Issue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.errors.map((err, i) => (
                    <TableRow key={i}>
                      <TableCell>{err.row}</TableCell>
                      <TableCell>{err.email}</TableCell>
                      <TableCell>{err.field}</TableCell>
                      <TableCell>{err.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Divider />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleReupload}>
                Re-upload File
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Step: preview_success ─────────────────────────────────── */}
        {step === "preview_success" && previewData && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="success">
              All <strong>{previewData.validCount}</strong> row
              {previewData.validCount !== 1 ? "s" : ""} are valid. Review
              below and click <strong>Import Now</strong> to proceed.
            </Alert>

            <Box sx={{ overflowX: "auto", maxHeight: "55vh", overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.preview.slice(0, visibleCount).map((row) => (
                    <TableRow key={row.row}>
                      <TableCell>{row.row}</TableCell>
                      <TableCell>{row.firstName ?? "-"}</TableCell>
                      <TableCell>{row.lastName ?? "-"}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.phoneNumber ?? "-"}</TableCell>
                      <TableCell>{row.notes || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Infinite scroll sentinel */}
              {visibleCount < previewData.preview.length && (
                <Box ref={sentinelRef} sx={{ height: 1 }} />
              )}
            </Box>

            <Divider />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button variant="outlined" onClick={handleReupload}>
                Re-upload
              </Button>
              <Button variant="contained" onClick={handleImport}>
                Import Now
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Step: importing ───────────────────────────────────────── */}
        {step === "importing" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              mt: 6,
            }}
          >
            <CircularProgress />
            <Typography>Importing participants…</Typography>
          </Box>
        )}

        {/* ── Step: done ────────────────────────────────────────────── */}
        {step === "done" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {importedCount > 0 ? (
              <Alert severity="success">
                Successfully imported <strong>{importedCount}</strong>{" "}
                participant{importedCount !== 1 ? "s" : ""}.
              </Alert>
            ) : (
              <Alert severity="warning">
                No participants were imported. All rows may already exist in the
                registry. Download the result CSV for details.
              </Alert>
            )}

            {resultCsvUrl ? (
              <Alert severity="info">
                A result CSV is available.{" "}
                <Button
                  size="small"
                  variant="text"
                  onClick={handleDownloadResult}
                  sx={{ p: 0, minWidth: 0, textDecoration: "underline" }}
                >
                  Download
                </Button>
              </Alert>
            ) : (
              <Alert severity="warning">
                The result CSV could not be generated this time. The import was
                still successful.
              </Alert>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={handleClose}>
                Close
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ImportParticipantsDrawer;
